/**
 * Normalises API failures into one shape the UI can render.
 *
 * The WorkWise endpoints answer 400 with a FLAT dict keyed by camelCase dotted
 * paths ({"customer.number": ["..."], "non_field_errors": ["..."]}); DRF's
 * default shape ({"detail": "..."}, nested dicts, bare arrays) is handled too.
 * Only strings from the body are surfaced, never the raw body, so an HTML error
 * page or an unexpected object cannot leak into the UI.
 */

export type ApiErrorKind =
  | 'validation'
  | 'conflict'
  | 'auth'
  | 'not_found'
  | 'server'
  | 'network'
  | 'unknown'

export interface ParsedApiError {
  status: number | null
  kind: ApiErrorKind
  /** One human-readable sentence. */
  message: string
  /** { "dotted.path": ["message", ...] } */
  fieldErrors: Record<string, string[]>
  /** non_field_errors plus top-level detail/messages. */
  nonFieldErrors: string[]
}

const MAX_MESSAGE = 500

function clean(text: string): string {
  return text.length > MAX_MESSAGE ? `${text.slice(0, MAX_MESSAGE)}...` : text
}

function collect(
  value: unknown,
  path: string,
  out: Record<string, string[]>,
  general: string[],
): void {
  const push = (message: string) => {
    if (!message.trim()) return
    if (!path || path === 'non_field_errors' || path === 'detail') general.push(clean(message))
    else (out[path] ??= []).push(clean(message))
  }
  if (typeof value === 'string') {
    push(value)
  } else if (Array.isArray(value)) {
    value.forEach((item, index) => {
      if (typeof item === 'string') push(item)
      else if (item && typeof item === 'object') collect(item, path ? `${path}.${index}` : String(index), out, general)
    })
  } else if (value && typeof value === 'object') {
    for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
      collect(inner, path && !['non_field_errors', 'detail'].includes(key) ? `${path}.${key}` : key, out, general)
    }
  }
}

function kindFor(status: number | null): ApiErrorKind {
  if (status === null) return 'network'
  if (status === 400 || status === 422) return 'validation'
  if (status === 401 || status === 403) return 'auth'
  if (status === 404) return 'not_found'
  if (status === 409) return 'conflict'
  if (status >= 500) return 'server'
  return 'unknown'
}

const FALLBACK_MESSAGE: Record<ApiErrorKind, string> = {
  validation: 'The server rejected some of the values. Check the highlighted fields.',
  conflict: 'This request conflicts with an existing one.',
  auth: 'You are not signed in, or you are not allowed to do this.',
  not_found: 'The requested item was not found.',
  server: 'The server hit an error. Try again; if it keeps failing, contact the platform team.',
  network: 'Cannot reach the server. Check your connection and try again.',
  unknown: 'Something went wrong. Try again.',
}

export function parseApiError(error: unknown): ParsedApiError {
  const response =
    error && typeof error === 'object' && 'response' in error
      ? (error as { response?: { status?: number; data?: unknown } }).response
      : undefined
  const status = typeof response?.status === 'number' ? response.status : null
  const kind = kindFor(status)

  const fieldErrors: Record<string, string[]> = {}
  const nonFieldErrors: string[] = []
  const data = response?.data
  if (data && typeof data === 'object') collect(data, '', fieldErrors, nonFieldErrors)

  const firstField = Object.values(fieldErrors)[0]?.[0]
  const message = nonFieldErrors[0] ?? (kind === 'conflict' ? firstField : undefined) ?? FALLBACK_MESSAGE[kind]
  return { status, kind, message, fieldErrors, nonFieldErrors }
}
